import { Box, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IDelayEditorProps {
    valueMs?: number;
    onChange: (ms: number) => void;
    label?: string;
    helperText?: string;
    size?: 'small' | 'medium';
}

function msToHms(ms: number): { hours: number; minutes: number; seconds: number } {
    const totalSeconds = Math.floor(Math.abs(ms) / 1000);
    return {
        hours: Math.floor(totalSeconds / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
    };
}

function hmsToMs(hours: number, minutes: number, seconds: number): number {
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
}

export default function DelayEditor(props: IDelayEditorProps) {
    const { t } = useTranslation("common");
    const size = props.size ?? 'small';

    const [hms, setHms] = useState(() =>
        props.valueMs !== undefined ? msToHms(props.valueMs) : { hours: 0, minutes: 0, seconds: 0 }
    );

    // Track the last ms value we sent to the parent so we can ignore our own echoes
    const lastExternalMsRef = useRef(props.valueMs);

    useEffect(() => {
        if (props.valueMs !== lastExternalMsRef.current) {
            lastExternalMsRef.current = props.valueMs;
            setHms(props.valueMs !== undefined ? msToHms(props.valueMs) : { hours: 0, minutes: 0, seconds: 0 });
        }
    }, [props.valueMs]);

    const handleFieldChange = (field: 'hours' | 'minutes' | 'seconds', rawValue: string) => {
        const parsed = parseInt(rawValue, 10);
        const value = isNaN(parsed) ? 0 : Math.max(0, field === 'hours' ? parsed : Math.min(59, parsed));
        const newHms = { ...hms, [field]: value };
        setHms(newHms);
        const newMs = hmsToMs(newHms.hours, newHms.minutes, newHms.seconds);
        lastExternalMsRef.current = newMs;
        props.onChange(newMs);
    };

    return (
        <Box>
            {props.label && (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                    {props.label}
                </Typography>
            )}
            <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                    label={t("delayEditor.hours")}
                    type="number"
                    size={size}
                    value={hms.hours}
                    inputProps={{ min: 0 }}
                    onChange={e => handleFieldChange('hours', e.target.value)}
                    style={{ width: 90 }}
                />
                <TextField
                    label={t("delayEditor.minutes")}
                    type="number"
                    size={size}
                    value={hms.minutes}
                    inputProps={{ min: 0, max: 59 }}
                    onChange={e => handleFieldChange('minutes', e.target.value)}
                    style={{ width: 90 }}
                />
                <TextField
                    label={t("delayEditor.seconds")}
                    type="number"
                    size={size}
                    value={hms.seconds}
                    inputProps={{ min: 0, max: 59 }}
                    onChange={e => handleFieldChange('seconds', e.target.value)}
                    style={{ width: 90 }}
                />
            </Box>
            {props.helperText && (
                <Typography variant="caption" color="textSecondary">
                    {props.helperText}
                </Typography>
            )}
        </Box>
    );
}
